/**
 * A build whose connection dropped (Bug 716). Closing the build stream does not stop the Chef
 * generation: it finishes the turn and saves its files. The page used to keep showing the live
 * build ("Checking it works…" with a running timer) under "The prototype builder stopped
 * responding", and only a reload picked the build back up. Now the panel settles as after Stop,
 * and the page picks the build up from the builder's saved files itself, as a reload does.
 *
 * Kept dependency-free so it stays unit-testable in the node test env.
 */

import type { BuildStreamError } from './buildStreamErrors';

/** Shown while the page waits for the builder to finish a build whose connection dropped. */
export const DROPPED_CONNECTION_MESSAGE =
  'The connection to the builder dropped. The build carries on, and it will show here when it is done.';

/** How often the page tries to pick the build up, and for how long. */
export const DROPPED_BUILD_PICKUP_INTERVAL_MS = 20_000;
export const DROPPED_BUILD_PICKUP_MAX_MS = 10 * 60_000;

/**
 * The stream broke after it started, on the way back from the builder: the network failed in
 * the middle of the response (`CHEF_UNAVAILABLE` from a thrown `TypeError`). A refused request
 * (an HTTP status) is not one: nothing ran, so there is nothing to pick up.
 */
export function isDroppedConnection(
  classified: BuildStreamError,
  args: { refusedWithStatus: boolean; streamStarted: boolean }
): boolean {
  return classified.code === 'CHEF_UNAVAILABLE' && !args.refusedWithStatus && args.streamStarted;
}

/** What the outcome of one pickup attempt (`resumeDraftFromServerFiles`) means. */
export type DroppedBuildPickupStep = 'try-again' | 'done' | 'give-up';

/**
 * - `files_unavailable`: the builder has not saved this turn's files yet (it saves them when it
 *   builds), so try again later.
 * - `skipped` while the version is still a draft: something else was busy (another resume, a
 *   stream); try again later. Any other status: another tab or device settled it. No version
 *   at all (the versions could not be read): nothing can be picked up, so stop waiting.
 * - every other outcome settled the version (built, failed, or could not run here), and shows
 *   its own state.
 * After `DROPPED_BUILD_PICKUP_MAX_MS` the page stops waiting and offers the usual actions.
 */
export function droppedBuildPickupStep(args: {
  outcome: string;
  versionStatus: string | undefined;
  elapsedMs: number;
}): DroppedBuildPickupStep {
  const { outcome, versionStatus, elapsedMs } = args;
  if (outcome === 'skipped' && versionStatus === undefined) return 'give-up';
  const waiting =
    outcome === 'files_unavailable' || (outcome === 'skipped' && versionStatus === 'draft');
  if (!waiting) return 'done';
  return elapsedMs + DROPPED_BUILD_PICKUP_INTERVAL_MS > DROPPED_BUILD_PICKUP_MAX_MS
    ? 'give-up'
    : 'try-again';
}
